// Permission catalog mirrored from prism-backend/src/core/permissions.py.
// Keep in sync with that file and the live /projects/{slug}/permissions
// endpoint — the live endpoint is the source of truth at runtime.

export const PERMISSION = {
  DASHBOARD_VIEW: "dashboard.view",

  COMPANIES_VIEW_ALL: "companies.view_all",
  COMPANIES_VIEW_SCORE: "companies.view_score",
  COMPANIES_CREATE: "companies.create",
  COMPANIES_EDIT: "companies.edit",
  COMPANIES_DELETE: "companies.delete",
  COMPANIES_ARCHIVE: "companies.archive",
  COMPANIES_IMPORT: "companies.import",
  COMPANIES_EXPORT: "companies.export",
  COMPANIES_RESCORE: "companies.rescore",
  COMPANIES_RESCORE_ALL: "companies.rescore_all",
  COMPANIES_CUSTOMIZE_LAYOUT: "companies.customize_layout",
  COMPANIES_CUSTOMIZE_FIELDS: "companies.customize_fields",
  COMPANIES_BULK_UPDATE: "companies.bulk_update",
  COMPANIES_BULK_DELETE: "companies.bulk_delete",

  CONTACTS_VIEW: "contacts.view",
  CONTACTS_CREATE: "contacts.create",
  CONTACTS_EDIT: "contacts.edit",
  CONTACTS_DELETE: "contacts.delete",
  CONTACTS_EXPORT: "contacts.export",

  ACTIVITY_VIEW: "activity.view",
  ACTIVITY_LOG_NOTE: "activity.log_note",

  CALLS_LOG: "calls.log",
  CALLS_START_TIMER: "calls.start_timer",

  CALL_CENTER_ACCESS: "call_center.access",
  CALL_CENTER_VIEW_STATS: "call_center.view_stats",
  CALL_CENTER_VIEW_TEAM: "call_center.view_team",

  NOTES_EDIT: "notes.edit",

  CALENDAR_VIEW: "calendar.view",
  CALENDAR_CREATE_EVENT: "calendar.create_event",

  ANALYTICS_VIEW: "analytics.view",
  ANALYTICS_EXPORT: "analytics.export",

  ENGINE_VIEW: "engine.view",
  ENGINE_TRIGGER: "engine.trigger",
  ENGINE_PAUSE_RESUME: "engine.pause_resume",
  ENGINE_CONFIGURE: "engine.configure",

  SETTINGS_VIEW: "settings.view",
  SETTINGS_MEMBERS_MANAGE: "settings.members.manage",
  SETTINGS_PROJECT_EDIT: "settings.project.edit",
  SETTINGS_SCORING_EDIT: "settings.scoring.edit",
  SETTINGS_ROLES_MANAGE: "settings.roles.manage",

  TAGS_VIEW: "tags.view",
  TAGS_MANAGE: "tags.manage",
  TAGS_ASSIGN: "tags.assign",

  OUTREACH_VIEW: "outreach.view",
  OUTREACH_ENROLL: "outreach.enroll",
  OUTREACH_PAUSE: "outreach.pause",
  OUTREACH_REMOVE: "outreach.remove",
  OUTREACH_MANAGE_CAMPAIGNS: "outreach.manage_campaigns",
  OUTREACH_SYNC: "outreach.sync",

  ENRICHMENT_ACCESS: "enrichment.access",
  ENRICHMENT_WORK: "enrichment.work",
  ENRICHMENT_VIEW_STATS: "enrichment.view_stats",
  ENRICHMENT_MANAGE_RATES: "enrichment.manage_rates",

  TASKS_VIEW: "tasks.view",
  TASKS_CREATE: "tasks.create",
  TASKS_EDIT: "tasks.edit",
  TASKS_DELETE: "tasks.delete",

  // Named `views.create` until the backend renamed it; the catalog has
  // only ever exposed `views.save`, so the old key matched no role and
  // the Save-view control was hidden for everyone, Admin included.
  VIEWS_SAVE: "views.save",
  VIEWS_SHARE: "views.share",
} as const;

export type PermissionKey = (typeof PERMISSION)[keyof typeof PERMISSION];

/**
 * Static fallback used by the Roles tab when the live /permissions
 * endpoint hasn't loaded yet. Keys must match the backend catalog.
 */
export const PERMISSION_CATALOG_FALLBACK: Array<{
  key: PermissionKey;
  group: string;
  label: string;
}> = [
  { key: PERMISSION.DASHBOARD_VIEW, group: "Dashboard", label: "View dashboard" },

  { key: PERMISSION.COMPANIES_VIEW_ALL, group: "Companies", label: "View all companies (otherwise only qualified)" },
  { key: PERMISSION.COMPANIES_VIEW_SCORE, group: "Companies", label: "View score and score breakdown" },
  { key: PERMISSION.COMPANIES_CREATE, group: "Companies", label: "Create companies" },
  { key: PERMISSION.COMPANIES_EDIT, group: "Companies", label: "Edit company fields, status, owner, notes, metadata" },
  { key: PERMISSION.COMPANIES_DELETE, group: "Companies", label: "Permanently delete archived companies" },
  { key: PERMISSION.COMPANIES_ARCHIVE, group: "Companies", label: "Archive / unarchive companies" },
  { key: PERMISSION.COMPANIES_IMPORT, group: "Companies", label: "Bulk import via CSV" },
  { key: PERMISSION.COMPANIES_EXPORT, group: "Companies", label: "Export companies to CSV" },
  { key: PERMISSION.COMPANIES_RESCORE, group: "Companies", label: "Rescore a single company" },
  { key: PERMISSION.COMPANIES_RESCORE_ALL, group: "Companies", label: "Rescore every company in the project" },
  { key: PERMISSION.COMPANIES_CUSTOMIZE_LAYOUT, group: "Companies", label: "Customize layout (reorder sections in company detail view)" },
  { key: PERMISSION.COMPANIES_CUSTOMIZE_FIELDS, group: "Companies", label: "Customize fields (show/hide and reorder fields within each section)" },
  { key: PERMISSION.COMPANIES_BULK_UPDATE, group: "Companies", label: "Bulk-update status / owner / tags on multiple companies" },
  { key: PERMISSION.COMPANIES_BULK_DELETE, group: "Companies", label: "Bulk-delete companies (irreversible)" },

  { key: PERMISSION.CONTACTS_VIEW, group: "Contacts", label: "View contacts" },
  { key: PERMISSION.CONTACTS_CREATE, group: "Contacts", label: "Create contacts" },
  { key: PERMISSION.CONTACTS_EDIT, group: "Contacts", label: "Edit contacts (incl. bulk-update)" },
  { key: PERMISSION.CONTACTS_DELETE, group: "Contacts", label: "Delete contacts (incl. bulk-delete)" },
  { key: PERMISSION.CONTACTS_EXPORT, group: "Contacts", label: "Export contacts to CSV" },

  { key: PERMISSION.ACTIVITY_VIEW, group: "Activity", label: "View the company activity timeline" },
  { key: PERMISSION.ACTIVITY_LOG_NOTE, group: "Activity", label: "Log a note (legacy)" },

  { key: PERMISSION.CALLS_LOG, group: "Calls", label: "Log a call" },
  { key: PERMISSION.CALLS_START_TIMER, group: "Calls", label: "Start the in-app call timer" },
  { key: PERMISSION.CALL_CENTER_ACCESS, group: "Calls", label: "Access the Call Center workspace" },
  { key: PERMISSION.CALL_CENTER_VIEW_STATS, group: "Calls", label: "View the Call Center Stats tab" },
  { key: PERMISSION.CALL_CENTER_VIEW_TEAM, group: "Calls", label: "View other operators' Call Center stats and history" },

  { key: PERMISSION.NOTES_EDIT, group: "Notes", label: "Edit company notes" },

  { key: PERMISSION.CALENDAR_VIEW, group: "Calendar", label: "View the calendar" },
  { key: PERMISSION.CALENDAR_CREATE_EVENT, group: "Calendar", label: "Create events on the calendar" },

  { key: PERMISSION.ANALYTICS_VIEW, group: "Analytics", label: "View the analytics page" },
  { key: PERMISSION.ANALYTICS_EXPORT, group: "Analytics", label: "Export analytics (PDF)" },

  { key: PERMISSION.ENGINE_VIEW, group: "Engine", label: "View jobs and runs" },
  { key: PERMISSION.ENGINE_TRIGGER, group: "Engine", label: "Manually trigger a job" },
  { key: PERMISSION.ENGINE_PAUSE_RESUME, group: "Engine", label: "Pause / resume a job" },
  { key: PERMISSION.ENGINE_CONFIGURE, group: "Engine", label: "Edit cron, timezone, params, scheduling window" },

  { key: PERMISSION.SETTINGS_VIEW, group: "Settings", label: "View project settings" },
  { key: PERMISSION.SETTINGS_MEMBERS_MANAGE, group: "Settings", label: "Invite, remove, change member roles" },
  { key: PERMISSION.SETTINGS_PROJECT_EDIT, group: "Settings", label: "Edit project name and description" },
  { key: PERMISSION.SETTINGS_SCORING_EDIT, group: "Settings", label: "Edit scoring rules and exclusions" },
  { key: PERMISSION.SETTINGS_ROLES_MANAGE, group: "Settings", label: "Create / edit / delete roles" },

  { key: PERMISSION.TAGS_VIEW, group: "Tags", label: "View tags on companies and in filters" },
  { key: PERMISSION.TAGS_MANAGE, group: "Tags", label: "Create, rename, recolor, delete tags" },
  { key: PERMISSION.TAGS_ASSIGN, group: "Tags", label: "Add or remove tags on a company" },

  { key: PERMISSION.OUTREACH_VIEW, group: "Outreach", label: "View outreach" },
  { key: PERMISSION.OUTREACH_ENROLL, group: "Outreach", label: "Enroll leads" },
  { key: PERMISSION.OUTREACH_PAUSE, group: "Outreach", label: "Pause / resume sequences" },
  { key: PERMISSION.OUTREACH_REMOVE, group: "Outreach", label: "Remove from campaign" },
  { key: PERMISSION.OUTREACH_MANAGE_CAMPAIGNS, group: "Outreach", label: "Manage email campaigns" },
  { key: PERMISSION.OUTREACH_SYNC, group: "Outreach", label: "Sync with Lemlist" },

  { key: PERMISSION.ENRICHMENT_ACCESS, group: "Enrichment", label: "Access the Enrichment workstation" },
  { key: PERMISSION.ENRICHMENT_WORK, group: "Enrichment", label: "Submit enrichments and earn from the rate card" },
  { key: PERMISSION.ENRICHMENT_VIEW_STATS, group: "Enrichment", label: "View the Enrichment Stats & Earnings tab" },
  { key: PERMISSION.ENRICHMENT_MANAGE_RATES, group: "Enrichment", label: "Edit per-field and per-member rate cards" },

  { key: PERMISSION.TASKS_VIEW, group: "Tasks", label: "View tasks on companies and in the Tasks page" },
  { key: PERMISSION.TASKS_CREATE, group: "Tasks", label: "Create tasks" },
  { key: PERMISSION.TASKS_EDIT, group: "Tasks", label: "Edit, complete, reopen tasks" },
  { key: PERMISSION.TASKS_DELETE, group: "Tasks", label: "Delete tasks" },

  { key: PERMISSION.VIEWS_SAVE, group: "Views", label: "Save filter views (and edit / delete one's own)" },
  { key: PERMISSION.VIEWS_SHARE, group: "Views", label: "Share a saved view with the team" },
];

/** Stable ordering of categories in the role-edit grid. */
export const PERMISSION_GROUP_ORDER = [
  "Dashboard",
  "Analytics",
  "Companies",
  "Contacts",
  "Activity",
  "Calls",
  "Notes",
  "Calendar",
  "Tasks",
  "Engine",
  "Tags",
  "Enrichment",
  "Views",
  "Settings",
];
